/**
 * Live cursor presence for staff PDF/image document viewers.
 *
 * Each active user writes a single doc at
 * `documentViewerPresence/{documentId}/presence/{uid}` with cursor position in
 * normalized page coordinates (0–100), throttled by the caller.
 */
import { db } from './firebase';
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { BOARD_PRESENCE_STALE_MS, cursorColorForUid } from './boardPresenceService';

export { cursorColorForUid, BOARD_PRESENCE_STALE_MS as DOCUMENT_PRESENCE_STALE_MS };

/**
 * Firestore path segments cannot contain `/` (or `//` from https URLs).
 * Comment attachments pass a download URL as `documentId` — collapse that to a
 * stable room id so viewers of the same file still share cursors.
 */
export function documentPresenceRoomId(documentId: string): string {
  const raw = (documentId || '').trim();
  if (!raw) return '_unknown';
  let key = raw;
  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw);
      const m = u.pathname.match(/\/o\/(.+)$/);
      if (m?.[1]) key = decodeURIComponent(m[1]);
    } catch {
      /* keep raw */
    }
  }
  if (!key.includes('/')) return key.slice(0, 700);
  return `p_${key.replace(/\//g, '__')}`.slice(0, 700);
}

export interface DocumentCursorPresence {
  uid: string;
  name: string;
  color: string;
  /** Normalized page X (0–100). */
  x: number;
  /** Normalized page Y (0–100). */
  y: number;
  pageNumber: number;
  updatedAt: Date | null;
}

function presenceDoc(documentId: string, uid: string) {
  return doc(db, 'documentViewerPresence', documentPresenceRoomId(documentId), 'presence', uid);
}

export function isFreshDocumentCursor(c: DocumentCursorPresence, now = Date.now()): boolean {
  return !!c.updatedAt && now - c.updatedAt.getTime() < BOARD_PRESENCE_STALE_MS;
}

/** All peer cursors (self excluded). Staleness is re-checked by the caller. */
export function subscribeDocumentPresence(
  documentId: string,
  selfUid: string,
  callback: (cursors: DocumentCursorPresence[]) => void,
): () => void {
  return onSnapshot(
    collection(db, 'documentViewerPresence', documentPresenceRoomId(documentId), 'presence'),
    snap => {
      const cursors = snap.docs
        .map(d => {
          const v = d.data();
          const updatedAtRaw = v.updatedAt as { toDate?: () => Date } | undefined;
          return {
            uid: d.id,
            name: String(v.name || 'Someone'),
            color: String(v.color || '#6366f1'),
            x: Number(v.x) || 0,
            y: Number(v.y) || 0,
            pageNumber: Number(v.pageNumber) || 1,
            updatedAt: updatedAtRaw?.toDate?.() ?? null,
          };
        })
        .filter(c => c.uid !== selfUid);
      callback(cursors);
    },
    err => {
      console.warn('[documentPresenceService] subscribe failed', err);
      callback([]);
    },
  );
}

export async function publishDocumentCursor(
  documentId: string,
  cursor: {
    uid: string;
    name: string;
    color: string;
    x: number;
    y: number;
    pageNumber: number;
  },
): Promise<void> {
  try {
    await setDoc(
      presenceDoc(documentId, cursor.uid),
      {
        name: cursor.name,
        color: cursor.color,
        x: Math.round(cursor.x * 10) / 10,
        y: Math.round(cursor.y * 10) / 10,
        pageNumber: cursor.pageNumber,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  } catch (err) {
    console.warn('[documentPresenceService] publish failed', err);
  }
}

export async function clearDocumentCursor(documentId: string, uid: string): Promise<void> {
  try {
    await deleteDoc(presenceDoc(documentId, uid));
  } catch {
    /* stale-filtering handles missed deletes */
  }
}

/** Convert pointer client coords to normalized page space (0–100). */
export function pointerToPageNorm(
  clientX: number,
  clientY: number,
  rect: DOMRect,
): { x: number; y: number } {
  const x = ((clientX - rect.left) / rect.width) * 100;
  const y = ((clientY - rect.top) / rect.height) * 100;
  return {
    x: Math.max(0, Math.min(100, x)),
    y: Math.max(0, Math.min(100, y)),
  };
}
